/**
 * Daily Omen quick-draw (STA-148).
 *
 * One line + one card per day. Verdict is one of:
 *   super_lucky / lucky / trust / caution
 *
 * Now backed by /daily-omen/{today,POST} — STA-148 retired the
 * localStorage seed-roll and the verdict / card / Stardust grant
 * all come from the server.
 *
 * The Friends wall (STA-150) reads from the same backend rows via
 * the friend-wall aggregator; this component dispatches a
 * ``mp-omen-drawn`` custom event after a successful draw so the
 * CheckinPill streak widget can refresh without a route change.
 */

import { useEffect, useMemo, useState } from 'react';
import {
  api,
  ApiError,
  formatApiError,
  type DailyOmenRecord,
  type Verdict,
} from '../api';
import { useAuth } from '../auth';
import { useI18n } from '../i18n';

// Re-export Verdict so existing call sites (Friends.tsx) that import
// it from this module compile cleanly until they're rewired to pull
// from ./api directly (STA-150).
export type { Verdict } from '../api';

const VERDICT_TONE: Record<Verdict, { color: string; glow: string; stars: number }> = {
  super_lucky: { color: '#E0AF68', glow: 'rgba(224,175,104,0.55)', stars: 5 },
  lucky: { color: '#9BD8E8', glow: 'rgba(155,216,232,0.45)', stars: 4 },
  trust: { color: '#C9A4D9', glow: 'rgba(201,164,217,0.45)', stars: 3 },
  caution: { color: '#9690B5', glow: 'rgba(150,144,181,0.4)', stars: 2 },
};

/** Public reader so Friends.tsx can hoist your own draw without an
 *  extra fetch. Cache is repopulated on each successful draw + on
 *  the omenToday() resolve so consumers always see the latest. */
let _cachedToday: DailyOmenRecord | null = null;
export function getMyDailyOmen(): DailyOmenRecord | null {
  return _cachedToday;
}

export default function DailyOmen() {
  const { refresh } = useAuth();
  const { t, lang } = useI18n();
  const [today_, setToday] = useState<DailyOmenRecord | null>(null);
  const [note, setNote] = useState(lang === 'en' ? 'checking in' : '打卡');
  const [flipping, setFlipping] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load today's omen on mount.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const data = await api.omenToday();
        if (cancelled) return;
        _cachedToday = data.log;
        setToday(data.log);
      } catch {
        // Best-effort — if /today fails (e.g. auth not yet loaded),
        // leave the UI in the "not drawn" state. The Draw button will
        // surface the real error if the user tries to submit.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Re-default the note placeholder when the language switches before
  // the user has drawn. Once drawn, the saved note wins.
  useEffect(() => {
    if (today_) return;
    setNote(lang === 'en' ? 'checking in' : '打卡');
  }, [lang, today_]);

  const tone = useMemo(() => (today_ ? VERDICT_TONE[today_.verdict] : null), [today_]);

  const draw = async () => {
    const txt = note.trim();
    if (!txt || today_) return;
    setError(null);
    setFlipping(true);
    try {
      const drawn = await api.drawOmen(txt);
      _cachedToday = drawn;
      setToday(drawn);
      // Notify peers (CheckinPill streak widget, Friends wall, etc.).
      window.dispatchEvent(new CustomEvent('mp-omen-drawn', { detail: drawn }));
      // Refresh user so the Stardust pill in the nav reflects the grant.
      void refresh();
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        // Race: another tab drew first. Reload today's state instead
        // of surfacing the conflict to the user.
        try {
          const data = await api.omenToday();
          _cachedToday = data.log;
          setToday(data.log);
        } catch {
          setError('今日已抽過籤，請刷新看結果。');
        }
      } else {
        setError(formatApiError(e, '抽籤失敗，請稍後再試。'));
      }
    } finally {
      // Hold the flip animation briefly so the back→face transition
      // feels intentional even when the API returns instantly.
      setTimeout(() => setFlipping(false), 600);
    }
  };

  return (
    <section className="card relative overflow-hidden">
      <div className="text-center mb-4">
        <div className="mp-eyebrow">{t('landing.omen_eyebrow')}</div>
        <h2 className="mp-display text-xl sm:text-2xl text-tarot-gold mt-2">
          {t('landing.omen_title')}
        </h2>
      </div>

      <div className="flex justify-center mb-6">
        <div className="relative w-40 h-60 sm:w-44 sm:h-64 tarot-flip" aria-live="polite">
          <div
            className={`tarot-flip-inner ${today_ || flipping ? 'flipped' : ''}`}
          >
            <div className="tarot-back" />
            <div
              className="tarot-face flex-col text-center"
              style={tone ? { boxShadow: `0 8px 32px ${tone.glow}` } : undefined}
            >
              {today_ && tone ? (
                <>
                  <div
                    className="mp-display text-2xl"
                    style={{ color: tone.color }}
                  >
                    {t(`verdict.${today_.verdict}` as any)}
                  </div>
                  <div className="text-tarot-muted text-sm mt-1">
                    {t(`verdict.${today_.verdict}_cn` as any)}
                    {' · '}
                    {'★'.repeat(tone.stars)}
                  </div>
                  <div className="mt-3 text-tarot-text text-sm">
                    {lang === 'zh' ? today_.card_name_zh : today_.card_name_en}
                  </div>
                </>
              ) : (
                <div className="text-tarot-gold/70 mp-display text-3xl">✦</div>
              )}
            </div>
          </div>
        </div>
      </div>

      {today_ ? (
        <div className="text-center">
          <p className="mp-eyebrow">{t('omen.your_note')}</p>
          <p className="mt-2 mp-display italic text-lg text-tarot-text inline-block border-dashed border border-tarot-gold/30 px-4 py-2 rounded">
            &ldquo;{today_.note}&rdquo;
          </p>
          {today_.stardust_added > 0 && (
            <p className="text-tarot-gold text-sm mt-3">
              + ★ {today_.stardust_added} Stardust
            </p>
          )}
          <p className="text-tarot-muted text-xs mt-4">{t('omen.tomorrow')}</p>
        </div>
      ) : (
        <div className="max-w-md mx-auto">
          <input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={t('omen.placeholder')}
            maxLength={60}
            className="input text-center"
            aria-label={t('omen.placeholder')}
          />
          <button
            type="button"
            onClick={draw}
            disabled={!note.trim() || flipping}
            className="btn-primary w-full mt-3 tracking-widest"
          >
            {flipping ? '…' : t('omen.draw')}
          </button>
          {error && (
            <p className="text-red-400 text-sm mt-2 text-center">{error}</p>
          )}
        </div>
      )}
    </section>
  );
}
